/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { pixieWorldApiClient } from '@/shared/api/PixieWorldApiService'
import {
  accountAuthMagicLinkLoginRetrieve,
  useAccountLoginCreate, useAccountPasswordResetConfirmCreate, useAccountPasswordResetCreate, useAccountRegisterCreate, useAccountTokenRefreshCreate,
} from '@/shared/api/generated/authentication/authentication'
import { accountMeRetrieve } from '@/shared/api/generated/profile/profile'
import { storefrontAuthOnboardingLoginRetrieve } from '@/shared/api/generated/storefront-auth/storefront-auth'
import { AccountRegisterCreate201, AuthToken, LoginRequest, MagicLinkLoginResponse, PasswordResetConfirm, Registration, StorefrontMagicLinkLoginResponse, User } from '@/shared/api/models'

import { AuthState, AuthStorageHandler } from './AuthStorageHandler'
import { removeClientAuthCookie, setClientAuthCookie } from './cookies'
import { isExpiredToken } from './utils'

export type SocialProvider = 'google'
export type SocialAuthResponse = {
  status: string
  message: string
  access_token: string
  refresh_token: string
  user_id: string
  email: string
}

export type PixieWorldAuthContextType = {
  isAuthenticating: boolean
  isAuthenticated: boolean
  isModerator: boolean
  profile: User | null
  getAuthState: () => AuthState
  refetchProfile: () => void
  signIn: (credentials: LoginRequest) => Promise<AuthToken | undefined>
  signOut: () => void
  register: (data: Registration) => Promise<AccountRegisterCreate201 | undefined>
  requestPasswordReset: (email: string) => Promise<void>
  resetPassword: (token: string, email: string, password: string) => Promise<void>
  refreshToken: () => Promise<void>
  getAccessToken: () => string | null
  verifyMagicLink: (token: string) => Promise<MagicLinkLoginResponse | undefined>
  verifyOnboardingMagicLink: (token: string) => Promise<StorefrontMagicLinkLoginResponse | undefined>
  loginWithSocialProvider: (provider: SocialProvider, code: string, redirectUrl: string) => Promise<unknown>
}

export const PixieWorldAuthContext = createContext<PixieWorldAuthContextType>({
  isAuthenticating: false,
  isAuthenticated: false,
  isModerator: false,
  profile: null,
  getAuthState: () => 'signedOut',
  refetchProfile: () => { throw new Error('Not implemented') },
  signIn: async () => { throw new Error('Not implemented') },
  signOut: () => { throw new Error('Not implemented') },
  register: async () => { throw new Error('Not implemented') },
  requestPasswordReset: async () => { throw new Error('Not implemented') },
  resetPassword: async () => { throw new Error('Not implemented') },
  refreshToken: async () => { throw new Error('Not implemented') },
  getAccessToken: () => null,
  verifyMagicLink: async () => { throw new Error('Not implemented') },
  verifyOnboardingMagicLink: async () => { throw new Error('Not implemented') },
  loginWithSocialProvider: async () => { throw new Error('Not implemented') },
})

// Storage handler singleton 
const authStorage = new AuthStorageHandler(
  typeof window !== 'undefined' ? window.localStorage : null as unknown as Storage,
)

export const usePixieWorldAuthContext = (): PixieWorldAuthContextType => {
  const context = useContext(PixieWorldAuthContext)

  if (!context) {
    throw new Error('usePixieWorldAuthContext must be used within a PixieWorldAuthContextProvider')
  }

  return context
}

export const PixieWorldAuthContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (typeof window !== 'undefined') {
      return authStorage.getAuthState() === 'signedIn'
    }

    return false
  })

  const [isModerator, setIsModerator] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = authStorage.getProfile()

      return stored?.profile?.is_moderator || false
    }

    return false
  })

  const [profile, setProfile] = useState<User | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = authStorage.getProfile()

      return stored?.profile || null
    }

    return null
  })

  // Auth mutations
  const { mutateAsync: loginMutate } = useAccountLoginCreate()
  const { mutateAsync: registerMutate } = useAccountRegisterCreate()
  const { mutateAsync: requestResetMutate } = useAccountPasswordResetCreate()
  const { mutateAsync: resetPasswordMutate } = useAccountPasswordResetConfirmCreate()
  const { mutateAsync: refreshTokenMutate } = useAccountTokenRefreshCreate()

  // Listen for auth state changes from storage events
  useEffect(() => {
    const handleAuthChange = (event: CustomEvent<{ authState: AuthState }>) => {
      const newAuthState = event.detail.authState

      setIsAuthenticated(newAuthState === 'signedIn')
      
      if (newAuthState === 'signedOut') {
        setProfile(null)
        setIsModerator(false)
      }
    }

    // Listen for custom auth events
    window.addEventListener('storage_auth_change', handleAuthChange as EventListener)

    // Also listen for storage events from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'auth_module_auth_state') {
        const newAuthState = event.newValue as AuthState

        setIsAuthenticated(newAuthState === 'signedIn')
        
        if (newAuthState === 'signedOut') {
          setProfile(null)
          setIsModerator(false)
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage_auth_change', handleAuthChange as EventListener)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  const getAuthState = useCallback(() => {
    const currentAuthState = authStorage.getAuthState()

    if (currentAuthState === 'signedIn') {
      const accessToken = authStorage.getAccessToken()

      pixieWorldApiClient.setAccessToken(accessToken)
    }

    return currentAuthState
  }, [])

  const refetchProfile = useCallback(async () => {
    const userProfile = await accountMeRetrieve()

    if (userProfile) {
      setProfile(userProfile)
      authStorage.setProfile(userProfile)
    }
  }, [])

  const signIn = useCallback(async (credentials: LoginRequest) => {
    setIsAuthenticating(true)

    try {
      const loginBody = {
        email: credentials.email,
        password: credentials.password,
      }

      const response = await loginMutate({ data: loginBody })

      // Set access token in API client
      if ('access_token' in response) {
        authStorage.setAccessToken(response.access_token)
        pixieWorldApiClient.setAccessToken(response.access_token)
        setClientAuthCookie(response.access_token)
      }

      // Store refresh token
      if ('refresh_token' in response) {
        authStorage.setRefreshToken(response.refresh_token)
      }

      // Update auth state
      authStorage.setAuthState('signedIn')

      // Refetch profile
      await refetchProfile()

      return response
    } finally {
      setIsAuthenticating(false)
    }
  }, [loginMutate, refetchProfile])

  // Sign out implementation
  const signOut = useCallback(() => {
    pixieWorldApiClient.setAccessToken(null)
    removeClientAuthCookie()
    authStorage.clearAuthStorage()
    authStorage.clearProfile()
  }, [])

  // Registration implementation  
  const register = useCallback(async (data: Registration) => {
    setIsAuthenticating(true)

    try {
      const response = await registerMutate({ data })

      // Auto sign in after registration if tokens returned
      if ('access_token' in response && 'refresh_token' in response) {
        pixieWorldApiClient.setAccessToken(response.access_token)
        setClientAuthCookie(response.access_token)
        authStorage.setAccessToken(response.access_token)
        authStorage.setRefreshToken(response.refresh_token)
        authStorage.setAuthState('signedIn')
        await refetchProfile()

        return response
      }
    } finally {
      setIsAuthenticating(false)
    }
  }, [registerMutate, refetchProfile])

  // Token refresh implementation
  const refreshToken = useCallback(async () => {
    const currentRefreshToken = authStorage.getRefreshToken()

    if (!currentRefreshToken) {
      throw new Error('No refresh token available')
    }

    try {
      const response = await refreshTokenMutate({
        data: { refresh_token: currentRefreshToken },
      })

      if ('access_token' in response) {
        pixieWorldApiClient.setAccessToken(response.access_token)
        authStorage.setAccessToken(response.access_token)
        setClientAuthCookie(response.access_token)

        return
      }

      throw new Error('No access token in refresh response')
    } catch (error) {
      signOut()
      throw error
    }
  }, [refreshTokenMutate, signOut])

  // Get the current access token
  const getAccessToken = useCallback(() => {
    const currentToken = authStorage.getAccessToken()
    const isTokenExpired = currentToken ? isExpiredToken(currentToken) : true

    if (!isTokenExpired) {
      return currentToken
    }

    return null
  }, [])

  const loginWithSocialProvider = useCallback(async (provider: SocialProvider, code: string, redirectUrl: string) => {
    setIsAuthenticating(true)

    try {
      // Web-specific endpoint for Authorization Code flow
      const endpoint = '/api/account/social/google/web/'

      const response: SocialAuthResponse = await pixieWorldApiClient.post(endpoint, {
        code,
        redirectUrl,
      })

      if (response.access_token) {
        authStorage.setAccessToken(response.access_token)
        pixieWorldApiClient.setAccessToken(response.access_token)
        setClientAuthCookie(response.access_token)

        if (response.refresh_token) {
          authStorage.setRefreshToken(response.refresh_token)
        }

        authStorage.setAuthState('signedIn')
        await refetchProfile()

        return response
      } else {
        throw new Error('Social login failed: No access token in response')
      }
    } catch (error) {
      console.error('Social login error:', error)
      throw error
    } finally {
      setIsAuthenticating(false)
    }
  }, [refetchProfile])

  // Setup axios intercepter for pixieWorldApiClient 
  useEffect(() => {
    const interceptor = pixieWorldApiClient.setupTokenRefreshInterceptor(
      () => getAccessToken(),
      () => refreshToken(),
    )

    return () => {
      pixieWorldApiClient.removeTokenRefreshInterceptor(interceptor)
    }
  }, [getAccessToken, refreshToken])

  // Password reset implementations
  const requestPasswordReset = useCallback(async (email: string) => {
    await requestResetMutate({
      data: {
        email,
        redirectUrl: `${window.location.origin}/account/password-reset`,
      },
    })
  }, [requestResetMutate])

  const resetPassword = useCallback(async (uidb64: string, token: string, password: string) => {
    const resetBody: PasswordResetConfirm = {
      uidb64,
      token,
      new_password: password,
    }

    await resetPasswordMutate({ data: resetBody })
  }, [resetPasswordMutate])

  const handleMagicLinkTokens = useCallback(async (response: { success: boolean; access_token: string; refresh_token: string }) => {
    if (!response.success) {
      throw new Error('Magic link verification failed')
    }

    authStorage.setAccessToken(response.access_token)
    pixieWorldApiClient.setAccessToken(response.access_token)
    setClientAuthCookie(response.access_token)

    if (response.refresh_token) {
      authStorage.setRefreshToken(response.refresh_token)
    }

    authStorage.setAuthState('signedIn')
    await refetchProfile()
  }, [refetchProfile])

  const verifyMagicLink = useCallback(async (token: string) => {
    setIsAuthenticating(true)

    try {
      const response = await accountAuthMagicLinkLoginRetrieve(token)

      await handleMagicLinkTokens(response)

      return response
    } catch (error) {
      console.error('Magic link verification error:', error)
      throw error
    } finally {
      setIsAuthenticating(false)
    }
  }, [handleMagicLinkTokens])

  const verifyOnboardingMagicLink = useCallback(async (token: string) => {
    setIsAuthenticating(true)

    try {
      const response = await storefrontAuthOnboardingLoginRetrieve(token)

      await handleMagicLinkTokens(response)

      return response
    } catch (error) {
      console.error('Onboarding magic link verification error:', error)
      throw error
    } finally {
      setIsAuthenticating(false)
    }
  }, [handleMagicLinkTokens])

  // Validate stored auth on mount
  useEffect(() => {
    const validateAuth = async () => {
      const currentState = getAuthState()

      if (currentState === 'signedIn') {
        try {
          // Get access token and check if expired
          const accessToken = authStorage.getAccessToken()
          const isTokenExpired = accessToken ? isExpiredToken(accessToken) : true

          if (isTokenExpired) {
            // Try to refresh the token
            await refreshToken()
          } else {
            pixieWorldApiClient.setAccessToken(accessToken)
          }

          // Ensure we have profile data
          if (!profile) {
            await refetchProfile()
          }
        } catch (error) {
          console.error('Auth validation failed:', error)
          // If refresh fails or profile fetch fails, clear auth
          signOut()
        }
      }
    }

    validateAuth()
  }, [getAuthState, signOut, refreshToken])

  // Update moderator status when profile changes
  useEffect(() => {
    if (isAuthenticated && profile?.is_moderator) {
      setIsModerator(true)
    } else {
      setIsModerator(false)
    }
  }, [isAuthenticated, profile])

  const contextValue = useMemo(() => ({
    isAuthenticating,
    isAuthenticated,
    isModerator,
    profile,
    getAuthState,
    signIn,
    signOut,
    register,
    requestPasswordReset,
    resetPassword,
    refreshToken,
    getAccessToken,
    verifyMagicLink,
    verifyOnboardingMagicLink,
    refetchProfile,
    loginWithSocialProvider,
  }), [
    isAuthenticating,
    isAuthenticated,
    isModerator,
    profile,
    getAuthState,
    signIn,
    signOut,
    register,
    requestPasswordReset,
    resetPassword,
    refreshToken,
    getAccessToken,
    verifyMagicLink,
    verifyOnboardingMagicLink,
    refetchProfile,
    loginWithSocialProvider,
  ])

  return (
    <PixieWorldAuthContext.Provider value={contextValue}>
      {children}
    </PixieWorldAuthContext.Provider>
  )
}